<template>
  <div
    class="nav-user"
    :class="[mode]"
  >
    <a-popover
      v-model="show"
      :overlay-class-name="`nav-user-popover ${mode} ${isRuntimeMode ? 'runtime' : ''}`"
      placement="bottomRight"
    >
      <template
        v-if="!isGuest"
        slot="content"
      >
        <user-info-card
          :src="avatar"
          :page-mode="pageMode"
          :mode="mode"
        >
          <template #default>
            <slot :close="close" />
          </template>
          <template #logout>
            <slot name="logout" />
          </template>
        </user-info-card>
      </template>
      <span
        class="nav-user_button"
        @click="handleUserButtonClick"
      >
        <a-badge
          :count="taskNumber"
          :offset="[-32, -3]"
        >
          <img :src="renderingAvatar">
        </a-badge>
        <span class="nav-user-name">{{ renderingName }}</span>
        <a-tag
          v-if="isMock"
          color="orange"
        >{{ $t("k_e73e0041") }}</a-tag>
        <a-icon
          v-if="!isGuest && showArrow"
          type="down"
          :class="['nav-user_icon', { 'nav-user_icon-active': show }]"
        />
      </span>
    </a-popover>
  </div>
</template>

<script>
import { $t } from '@/i18n';
import { getUserId, getUserAvatarURL } from '@utils/userInfo';
import userInfoCard from './NavUserInfoCard.vue';
import { isGuest } from '@/utils/userInfo';
import { fetchStaffs } from '@/services/staff';
import stringInterop from '@tencent/ui-core/lib/utils/stringInterop';
import { loginUtil } from '@/pages/modules/sso/login';
import { isOffline } from '@/utils/offline';

export default {
  name: 'NavUser',
  components: {
    userInfoCard,
  },
  inject: {
    ctx: {
      from: 'ctx',
      default: null,
    },
  },
  props: {
    avatar: {
      type: String,
      default: getUserAvatarURL(),
    },
    userNameCustomInterop: {
      type: String,
      default: '',
    },
    username: {
      type: String,
      default: getUserId(),
    },
    mode: {
      type: String,
      default: 'light',
    },
    taskNumber: {
      type: Number,
      default: 0,
    },
    pageMode: {
      type: String,
      default: 'editor',
    },
    showArrow: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      displayName: this.username,
      show: false,
      isGuest: isGuest(),
    };
  },
  computed: {
    isRuntimeMode() {
      return this.ctx?.mode === 'runtime';
    },
    isMock() {
      return !!window.GLOBAL_INFO.mockedBy;
    },
    renderingName() {
      if (this.isGuest) {
        return this.displayName;
      }
      const getCustomName = () => {
        if (this.isRuntimeMode) {
          return stringInterop(this.userNameCustomInterop, {
            userInfo: window?.OPEN_RUNTIME_INFO?.userInfo || {},
            frontendData: window.w?.user?.frontendData || {},
            window,
          });
        }
        return $t('k_373f9513');
      };
      return this.userNameCustomInterop
        ? getCustomName()
        : this.displayName;
    },
    renderingAvatar() {
      return window.OPEN_RUNTIME_INFO?.userInfo?.avatar || this.avatar;
    },
  },
  mounted() {
    this.fetchChnName();
    // eslint-disable-next-line no-underscore-dangle
    if (window.__MICRO_APP_ENVIRONMENT__) {
      this.show = true;
      this.$nextTick(() => (this.show = false));
    }
  },
  methods: {
    handleUserButtonClick() {
      if (this.isGuest) {
        if (window.wujiFrontEndOptions.onLogin) {
          window.wujiFrontEndOptions.onLogin({ choosePort: loginUtil.choosePort });
          return;
        }
        window.location.href = '/sso/login';
        return;
      }
    },

    close() {
      this.show = false;
    },
    async fetchChnName() {
      if (this.isGuest) {
        this.displayName = $t('k_ae04823f');
        return;
      }
      if (isOffline()) return;

      try {
        const resp = await fetchStaffs(this.username);
        const account = resp[0];
        const userName = account?.chnName ?? window?.OPEN_RUNTIME_INFO?.userInfo?.name;
        if (userName) {
          this.displayName += `(${userName})`;
        }
      } catch (error) {
        console.error('fetch chn name failed', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.nav-user {
  margin: var(--xy-custom-nav-user-margin);
  line-height: 32px;
  img {
    display: var(--xy-custom-nav-user-avatar-display);
    width: var(--xy-custom-nav-user-avatar-size);
    height: var(--xy-custom-nav-user-avatar-size);
    margin-right: var(--xy-custom-nav-user-avatar-name-gap);
    border-radius: 100%;
  }
  &_button {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    .nav-user-name {
      display: var(--xy-custom-nav-user-name-display);
      font-size: var(--xy-custom-nav-font-size);
      line-height: 22px;
    }
  }
  &_icon {
    margin-left: var(--xy-custom-nav-user-name-icon-gap);
    display: var(--xy-custom-nav-user-icon-display) !important;
    font-size: 10px;
  }
  &.dark {
    pointer-events: none;
    .nav-user-name {
      color: #fff;
    }
    .nav-user_icon {
      display: none;
    }
  }
}

#xy-runtime-layout .header,
#xy-preview-layout .header {
  .nav-user {
    &_button,
    &_icon {
      color: var(--xy-custom-nav-color);
    }
    &.dark {
      pointer-events: auto;
      .nav-user_icon {
        display: inline-block;
      }
    }
    &_button {
      &:hover,
      &:hover .nav-user_icon {
        color: var(--xy-custom-nav-hover-color);
      }
      &.ant-popover-open,
      &.ant-popover-open .nav-user_icon {
        color: var(--xy-custom-nav-active-color);
      }
    }
  }
}
</style>

<style lang="scss">
.nav-user-popover .ant-popover-inner-content {
  padding: var(--xy-custom-nav-user-pop-padding);
  min-width: var(--xy-custom-nav-drop-pop-min-width);
}
</style>
